import { ASIconButton, ComponentSize, createGlyphIcon } from '@eleventheye/asui';
import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import styles from './Carousel.module.scss';
import { CarouselProps } from './Carousel.types';

const PlayIcon = createGlyphIcon({
  glyph: 'play_arrow',
  fontFamily: 'Material Symbols Rounded',
  weight: 300,
});
const PauseIcon = createGlyphIcon({ glyph: 'pause', fontFamily: 'Material Symbols Rounded', weight: 300 });

/**
 * Displays caller-provided content in mounted slides that always enter from the right.
 * Manual selection stops rotation until Play is pressed; hover, focus and reduced
 * motion also pause rotation. The parent supplies the carousel height and slide layout.
 * Inactive slides remain mounted but cannot receive keyboard focus.
 *
 * @param {CarouselProps} props - Slide content, accessible name and rotation timing.
 *
 * @return {React.ReactElement | null} Carousel with bar selectors and ASUI playback control, or nothing for an empty list.
 */
const Carousel: React.FC<CarouselProps> = ({
  items,
  ariaLabel,
  className = '',
  autoPlay = false,
  rotationPaused = false,
  intervalMs = 5000,
  transitionDurationMs = 1800,
  motionOpacity = 1,
}) => {
  const [slides, setSlides] = useState({ current: 0, previous: -1 });
  const selectedSlide = slides.current < items.length ? slides.current : 0;
  const [isPaused, setIsPaused] = useState(!autoPlay);
  const [isHovered, setIsHovered] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(true);
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updatePreference = () => setReducedMotion(preference.matches);
    updatePreference();
    preference.addEventListener('change', updatePreference);
    return () => preference.removeEventListener('change', updatePreference);
  }, []);

  useEffect(() => {
    if (rotationPaused || items.length < 2 || isPaused || isHovered || hasFocus || reducedMotion) return;
    const timer = window.setInterval(() => {
      setSlides(({ current }) => ({ current: (current + 1) % items.length, previous: current }));
    }, intervalMs);
    return () => window.clearInterval(timer);
  }, [rotationPaused, isPaused, isHovered, hasFocus, reducedMotion, items.length, intervalMs]);

  const selectSlide = (index: number) => {
    if (!items.length) return;
    const nextSlide = (index + items.length) % items.length;
    setSlides((current) =>
      nextSlide === current.current ? current : { current: nextSlide, previous: current.current },
    );
    setIsPaused(true);
  };

  if (!items.length) return null;

  return (
    <section
      className={`${styles.Carousel} ${className}`}
      style={
        {
          '--carousel-duration': `${transitionDurationMs}ms`,
          '--carousel-motion-opacity': Math.min(1, Math.max(0, motionOpacity)),
        } as CSSProperties
      }
      role="region"
      aria-roledescription="carousel"
      aria-label={ariaLabel}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocusCapture={() => setHasFocus(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setHasFocus(false);
      }}
      onKeyDown={(event) => {
        if (
          event.target !== event.currentTarget &&
          !(event.target as HTMLElement).closest('[data-carousel-controls]')
        )
          return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          selectSlide(selectedSlide + (event.key === 'ArrowRight' ? 1 : -1));
        }
      }}
    >
      <div
        className={styles.Slides}
        aria-live={isPaused || reducedMotion ? 'polite' : 'off'}
        onTouchStart={(event) => {
          const touch = event.touches[0];
          touchStart.current = { x: touch.clientX, y: touch.clientY };
        }}
        onTouchCancel={() => {
          touchStart.current = null;
        }}
        onTouchEnd={(event) => {
          const start = touchStart.current;
          touchStart.current = null;
          if (!start) return;
          const touch = event.changedTouches[0];
          const distanceX = touch.clientX - start.x;
          const distanceY = touch.clientY - start.y;
          if (Math.abs(distanceX) > 50 && Math.abs(distanceX) > Math.abs(distanceY)) {
            selectSlide(selectedSlide + (distanceX < 0 ? 1 : -1));
          }
        }}
      >
        <div className={styles.Track} data-transitioned={slides.previous !== -1}>
          {items.map((item, index) => (
            <div
              key={item.id}
              className={styles.Slide}
              data-position={
                index === selectedSlide ? 'current' : index === slides.previous ? 'previous' : 'offscreen'
              }
              aria-hidden={index !== selectedSlide}
              inert={index !== selectedSlide}
              role="group"
              aria-roledescription="slide"
              aria-label={`${index + 1} of ${items.length}: ${item.label}`}
            >
              {item.content}
            </div>
          ))}
        </div>
      </div>
      {items.length > 1 && (
        <div className={styles.Controls} data-carousel-controls>
          {items.map((item, index) => (
            <button
              key={item.id}
              type="button"
              className={styles.Indicator}
              aria-label={`Show ${item.label}`}
              aria-current={index === selectedSlide ? 'true' : undefined}
              onClick={() => selectSlide(index)}
            >
              <span />
            </button>
          ))}
          <ASIconButton
            as="button"
            type="button"
            size={ComponentSize.Small}
            iconSize={24}
            icon={isPaused || reducedMotion ? PlayIcon : PauseIcon}
            aria-label={isPaused || reducedMotion ? 'Play slideshow' : 'Pause slideshow'}
            disabled={reducedMotion}
            title={reducedMotion ? 'Automatic rotation disabled by reduced-motion preference' : undefined}
            onClick={() => setIsPaused((paused) => !paused)}
          />
        </div>
      )}
    </section>
  );
};

export default Carousel;
